/**
 * Hook para consumir la API front en tiempo real (src/services/front.js).
 * Útil en páginas/templates que deben mostrar datos actualizados (producto, categoría, home, etc.).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getHome,
  getProduct,
  search,
  getCategory,
  getBrand,
  getTag,
  getSupplier,
} from '../services/front';

const isBrowser = typeof window !== 'undefined';

/**
 * Estado genérico: data, loading, error.
 * Usa un ref para la función fetch de modo que el useCallback no dependa de ella.
 */
function useFetch(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  const run = useCallback(async () => {
    if (!isBrowser) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchRef.current();
      setData(result);
      return result;
    } catch (err) {
      console.error('[useFrontApi]', err);
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (isBrowser) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch: run };
}

/** Home: last_products, flash_offers, perfect_gift */
export function useHome(limit = 12) {
  return useFetch(() => getHome(limit), [limit]);
}

/** Detalle de producto por slug */
export function useProduct(slug) {
  return useFetch(() => (slug ? getProduct(slug) : Promise.resolve(null)), [slug]);
}

/** Búsqueda paginada. Si query está vacío o tiene menos de 2 caracteres, no llama a la API. */
export function useSearch(query, page = 1, perPage = 24) {
  const q = (query && String(query).trim()) || '';
  return useFetch(
    () => (q.length >= 2 ? search(q, page, perPage) : Promise.resolve({ data: [], current_page: 1, last_page: 1, per_page: perPage, total: 0 })),
    [q, page, perPage]
  );
}

/** Categoría por slug */
export function useCategory(slug) {
  return useFetch(() => (slug ? getCategory(slug) : Promise.resolve(null)), [slug]);
}

/** Marca por slug */
export function useBrand(slug) {
  return useFetch(() => (slug ? getBrand(slug) : Promise.resolve(null)), [slug]);
}

/** Etiqueta por slug */
export function useTag(slug) {
  return useFetch(() => (slug ? getTag(slug) : Promise.resolve(null)), [slug]);
}

/** Distribuidor por slug */
export function useSupplier(slug) {
  return useFetch(() => (slug ? getSupplier(slug) : Promise.resolve(null)), [slug]);
}

export { useFetch };